/**
 * Shell-only: parser for VITE_EXTERNAL_LINKS_CONFIG.
 *
 * The Portal renders a small "External Services" section below the
 * subsystem cards, grouped by `group`. Edits go in `apps/shell/.env`
 * (dev) or Vercel env (prod) — no code change required.
 *
 * Shape mirrors VITE_SERVICES_CONFIG for muscle memory, with a
 * required `group` field and no `comingSoon` (external services are
 * always live or omitted).
 */
export interface ExternalLink {
  id: string;
  name: string;
  url: string;
  desc?: string;
  /** Icon name from shared-ui's REGISTRY (`Monitor` / `Shield` / `Zap` / ...). */
  iconName?: string;
  /** Display group (e.g. "RPC", "Hosting"). Missing → "Other". */
  group?: string;
  /** Optional explicit logo URL (PNG/SVG). When set, takes precedence over
   *  the auto-derived favicon. Use for brand-correct logos (e.g.
   *  https://cdn.simpleicons.org/vercel). */
  logo?: string;
}

interface ExternalLinkConfigEntry {
  id: string;
  name: string;
  url: string;
  desc?: string;
  icon?: string;
  iconName?: string;
  group?: string;
  logo?: string;
}

export function parseExternalLinks(raw: string | undefined | null): ExternalLink[] {
  if (!raw || raw.trim() === '') return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.warn('[external-links] VITE_EXTERNAL_LINKS_CONFIG is not valid JSON:', e);
    return [];
  }

  if (!Array.isArray(parsed)) {
    console.warn('[external-links] VITE_EXTERNAL_LINKS_CONFIG must be a JSON array');
    return [];
  }

  const seen = new Map<string, ExternalLink>();
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Partial<ExternalLinkConfigEntry>;
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    const url = typeof raw.url === 'string' ? raw.url.trim() : '';
    if (!id || !name || !url) {
      console.warn('[external-links] dropping entry — missing id/name/url', raw);
      continue;
    }
    seen.set(id, {
      id,
      name,
      url,
      desc: typeof raw.desc === 'string' ? raw.desc : undefined,
      iconName:
        typeof raw.iconName === 'string' && raw.iconName
          ? raw.iconName
          : typeof raw.icon === 'string' && raw.icon
            ? raw.icon
            : undefined,
      group: typeof raw.group === 'string' && raw.group ? raw.group : undefined,
      logo: typeof raw.logo === 'string' && raw.logo ? raw.logo : undefined,
    });
  }
  return Array.from(seen.values());
}

/**
 * Resolve a logo URL for an entry.
 *   1. Explicit `logo` field (PNG/SVG URL) — best fidelity
 *   2. Auto-derived favicon via DuckDuckGo's icon CDN — works for any
 *      public URL with a reasonable favicon, no API key
 *   Falls back to null when both unavailable; caller should render an
 *   IconByName / lucide icon in that case.
 */
export function logoUrlFor(link: ExternalLink): string | null {
  if (link.logo) return link.logo;
  try {
    const host = new URL(link.url).hostname;
    if (!host) return null;
    return `https://icons.duckduckgo.com/ip3/${host}.ico`;
  } catch {
    return null;
  }
}

/**
 * Group links preserving first-seen group order so the env config
 * controls display order (RPC before Hosting before …).
 */
export function groupExternalLinks(links: ExternalLink[]): { name: string; items: ExternalLink[] }[] {
  const order: string[] = [];
  const map = new Map<string, ExternalLink[]>();
  for (const link of links) {
    const key = link.group ?? '其他';
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(link);
  }
  return order.map((name) => ({ name, items: map.get(name)! }));
}
